/**
 * A party-wise month on the Reconciliation screen (W3-09 e).
 *
 * A party-wise purchase register is one row per supplier with no invoice
 * numbers, so the month is reconciled supplier by supplier (D-33). Each
 * supplier is claimed as its 2B carries it, credit notes reducing it (D-100,
 * D-101; until W3-18 (4) the lesser of the register and the 2B, D-34). The
 * service sends the ledger beside the claim; this turns it
 * into what a person reads — a status in words, the excess named, a suggested
 * pairing they can accept and an accepted one they can undo. It computes no
 * figure: every amount here is one the service sent.
 */

import type { ReconState } from './api'

export const HEADS = ['igst', 'cgst', 'sgst', 'cess'] as const
export type Head = (typeof HEADS)[number]
export type Amounts = Partial<Record<Head | 'taxable', number>>

export interface SupplierLine {
  /** The 2B's GSTIN where an accepted pairing joined the register to it. */
  gstin: string
  name: string | null
  books: Amounts
  tb: Amounts
  eligible: Amounts
  status: string
  /** Per head, how far the register exceeds the 2B — claimed nowhere, never carried (D-45). */
  excess: Amounts
  /** Register GSTINs an accepted pairing joined to this line. */
  joined: string[]
  /** The one 2B GSTIN a checksum-failing register GSTIN most nearly is (D-44). */
  suggestion: string | null
  gstin_invalid: boolean
}

/** A 2B row listed beside the ledger: a credit note, or a row not counted. */
export interface LedgerNote extends Amounts {
  supplier_gstin: string
  doc_no: string | null
  doc_date: string | null
  doc_kind: string
  why?: string
}

export interface Pairing {
  books_gstin: string
  tb_gstin: string
  action_id: string
}

export interface SupplierLedgerOut {
  lines: SupplierLine[]
  eligible: Amounts
  books_total: Amounts
  tb_total: Amounts
  credit_notes: LedgerNote[]
  not_counted: LedgerNote[]
  pairings: Pairing[]
  /**
   * False when no GSTR-2B is imported for the month (N27): every supplier then reads
   * `not_on_2b`, and nothing is known about any of them. Set by `supplierLedgerOf`.
   */
  imported?: boolean
}

/** Every status the service can send — `supplier_statuses` in the seam vocabularies. */
export const SUPPLIER_STATUS_LABELS = {
  matched: 'Register within the 2B',
  register_exceeds_2b: 'Register exceeds the 2B',
  not_on_2b: 'Not on the 2B',
  only_on_2b: 'On the 2B, not in the register',
} as const

/** Why a 2B row is listed and not counted — `supplier_not_counted` in the seam vocabularies. */
export const NOT_COUNTED_LABELS = {
  amendment: 'Amends an earlier month’s document, which that month’s register carries',
  itc_not_available: 'The portal marks this credit as not available',
  no_supplier: 'Names no supplier GSTIN',
} as const

/** Indian digit grouping, paise in — the same reading as the rest of the screen. */
function rupees(paise: number): string {
  return (paise / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** The four heads summed — the one figure a row shows per side; the heads ride its tooltip. */
export function taxOf(amounts: Amounts | undefined): number {
  return HEADS.reduce((sum, head) => sum + (amounts?.[head] ?? 0), 0)
}

/** `IGST 0.00 · CGST 2,500.00 · …`, only the heads that carry something. */
export function headsInWords(amounts: Amounts | undefined): string {
  const parts = HEADS.filter((head) => (amounts?.[head] ?? 0) !== 0).map(
    (head) => `${head.toUpperCase()} ${rupees(amounts![head]!)}`,
  )
  return parts.length ? parts.join(' · ') : 'No tax'
}

/**
 * The status label, never the slug: a status with no words yet says so rather than leaking it.
 * With no 2B imported, "Not on the 2B" asserts an absence nobody checked (N27).
 */
export function statusLabel(status: string, imported = true): string {
  if (!imported && status === 'not_on_2b') return 'No 2B imported yet'
  return (SUPPLIER_STATUS_LABELS as Record<string, string>)[status] ?? 'A status with no words yet'
}

/** Before any 2B is imported (N27): the line is not known to be unfiled, only unchecked. */
const NO_2B_YET =
  'No GSTR-2B is imported for this month yet, so whether the supplier filed is not known. ' +
  'Nothing is claimed until it is.'

export function notCountedLabel(why: string | undefined): string {
  return (why && (NOT_COUNTED_LABELS as Record<string, string>)[why]) || 'Not counted'
}

/**
 * What a `matched` line means for the claim (N34) — `supplier_matched_sentences` in the seam
 * vocabularies, which the reconciliation download prints in the same words. "Within the 2B"
 * includes below it, so "agrees on every head" is said only when it is equal on every head;
 * `{amount}` is the shortfall, written as this screen writes a line's excess.
 */
export const MATCHED_SENTENCES = {
  agrees: 'Claimed as the 2B carries it; the register agrees on every head',
  short:
    'Claimed as the 2B carries it; the register is {amount} less than the 2B — ' +
    'ask the client to book the rest',
} as const

/**
 * Paise of tax the 2B carries and the register does not, summed over the heads — the service's
 * `shortfall`. A head the register is over is not netted against one it is under.
 */
export function shortfallOf(line: Pick<SupplierLine, 'books' | 'tb'>): number {
  return HEADS.reduce(
    (sum, head) => sum + Math.max((line.tb?.[head] ?? 0) - (line.books?.[head] ?? 0), 0),
    0,
  )
}

/**
 * What the status means for the claim, in one sentence. With the ledger, a 2B supplier the
 * register carries under a mistyped GSTIN says so — "the register does not carry it" would be
 * the wrong sentence on the very line a pairing is waiting for.
 */
export function statusSentence(line: SupplierLine, ledger?: SupplierLedgerOut): string {
  switch (line.status) {
    case 'matched': {
      const short = shortfallOf(line)
      return short
        ? `${MATCHED_SENTENCES.short.replace('{amount}', `₹${rupees(short)}`)}.`
        : `${MATCHED_SENTENCES.agrees}.`
    }
    case 'register_exceeds_2b':
      return (
        `The register is ₹${rupees(taxOf(line.excess))} more than the 2B. The 2B ` +
        'is claimed; the rest is not carried to a later month.'
      )
    case 'not_on_2b':
      if (ledger?.imported === false) {
        return line.gstin_invalid ? `This GSTIN fails the checksum. ${NO_2B_YET}` : NO_2B_YET
      }
      if (line.suggestion) {
        return (
          'This GSTIN fails the checksum and looks like a mistyping of ' +
          `${line.suggestion}, which is on the 2B. The 2B is claimed either way; pair them to ` +
          'compare the register with it.'
        )
      }
      return line.gstin_invalid
        ? 'This GSTIN fails the checksum, and no single 2B GSTIN is close enough to suggest. No 2B carries it, so nothing is claimed.'
        : 'The supplier has filed nothing for this month under this GSTIN. Nothing is claimed.'
    case 'only_on_2b': {
      const typed = ledger?.lines.filter((other) => other.suggestion === line.gstin) ?? []
      if (typed.length) {
        return (
          `The register carries this supplier as ${typed.map((t) => t.gstin).join(', ')}, a ` +
          'mistyped GSTIN. The 2B is claimed; pair them to compare the register with it.'
        )
      }
      return 'The 2B carries this supplier and the register does not. It is claimed; ask the client to book it.'
    }
    default:
      return ''
  }
}

/** The supplier's name as the ledger has it — a credit note names only a GSTIN. */
export function nameOf(ledger: SupplierLedgerOut, gstin: string): string | null {
  return ledger.lines.find((line) => line.gstin === gstin)?.name ?? null
}

/** `1 register within the 2B · 2 not on the 2B · …` — the label's first letter lowered only. */
export function summaryWords(summary: LedgerSummary): string {
  const parts = summary.byStatus.map(
    (s) => `${s.count} ${s.label.charAt(0).toLowerCase()}${s.label.slice(1)}`,
  )
  if (summary.waiting > 0) {
    parts.push(
      `${summary.waiting} suggested GSTIN ${summary.waiting === 1 ? 'pairing waits' : 'pairings wait'} on you`,
    )
  }
  return parts.join(' · ')
}

/** The pairing that joined `books_gstin` to its line — the action an Undo reverts. */
export function pairingOf(ledger: SupplierLedgerOut, booksGstin: string): Pairing | undefined {
  return ledger.pairings.find((p) => p.books_gstin === booksGstin)
}

export interface LedgerSummary {
  suppliers: number
  /** Suggested pairings waiting on a person. */
  waiting: number
  byStatus: { status: string; label: string; count: number }[]
}

/** Counts, in the order the statuses are declared, only those present. */
export function ledgerSummary(ledger: SupplierLedgerOut): LedgerSummary {
  const counts = new Map<string, number>()
  for (const line of ledger.lines) counts.set(line.status, (counts.get(line.status) ?? 0) + 1)
  const known = Object.keys(SUPPLIER_STATUS_LABELS)
  const order = [...known, ...[...counts.keys()].filter((s) => !known.includes(s))]
  return {
    suppliers: ledger.lines.length,
    waiting: ledger.lines.filter((line) => line.suggestion).length,
    byStatus: order
      .filter((status) => counts.has(status))
      .map((status) => ({
        status,
        label: statusLabel(status, ledger.imported !== false),
        count: counts.get(status)!,
      })),
  }
}

/** The ledger, when the month is reconciled at supplier level; null otherwise. */
export function supplierLedgerOf(state: ReconState | null): SupplierLedgerOut | null {
  if (!state || state.mode !== 'supplier' || !state.suppliers) return null
  const s = state.suppliers
  return {
    ...s,
    lines: s.lines ?? [],
    credit_notes: s.credit_notes ?? [],
    not_counted: s.not_counted ?? [],
    pairings: s.pairings ?? [],
    imported: state.import != null,
  }
}
